import ApiService from "./api";

// Individual import record
export interface InventoryItem {
  id: number;
  merchant_id: number;
  merchant_name: string;
  business_name: string;
  import_batch_id: string;
  import_date: string;
  imported_by: {
    id: number;
    name: string;
  } | null;
  sku: string;
  product_code: string;
  product_name: string;
  product_description: string | null;
  manufacturer: string | null;
  quantity: number;
  original_quantity: number;
  price: number;
  note: string | null;
  quantity_status: "under_review" | "received" | "missing_quantity";
  missing_quantity_number: number;
  available_quantity: number;
  created_at: string;
  updated_at: string;
}

// Dashboard SKU aggregated view
export interface DashboardInventoryItem {
  sku: string;
  product_code: string;
  product_name: string;
  product_description: string | null;
  manufacturer: string | null;
  original_quantity: number;
  total_quantity: number;
  price: number;
  import_count: number;
  pending_imports: number;
  last_import_date: string;
}

export interface InventoryStatistics {
  total_items: number;
  under_review: number;
  received: number;
  missing_quantity: number;
  total_quantity: number;
  total_value: number;
}

export interface ImportInventoryResponse {
  message: string;
  imported_count: number;
  import_batch_id: string;
}

// Get merchant dashboard (SKU aggregated view)
export const getInventoryDashboard = (
  merchantId: number,
  filter?: {
    search?: string;
    per_page?: number;
    manufacturer?: string;
    min_price?: number;
    max_price?: number;
    min_quantity?: number;
    max_quantity?: number;
    has_pending?: boolean;
    sort_by?: string;
    sort_order?: "asc" | "desc";
  },
  page: number = 1
): Promise<any> => {
  return ApiService.fetchData({
    url: `inventory/${merchantId}/dashboard`,
    method: "GET",
    params: {
      search: filter?.search,
      per_page: filter?.per_page || 15,
      page: page,
      manufacturer: filter?.manufacturer,
      min_price: filter?.min_price,
      max_price: filter?.max_price,
      min_quantity: filter?.min_quantity,
      max_quantity: filter?.max_quantity,
      has_pending: filter?.has_pending ? "true" : undefined,
      sort_by: filter?.sort_by,
      sort_order: filter?.sort_order,
    },
  });
};

// Get all import records for a specific SKU
export const getSkuImports = (
  merchantId: number,
  sku: string
): Promise<{ data: InventoryItem[] }> => {
  return ApiService.fetchData({
    url: `inventory/${merchantId}/sku/${encodeURIComponent(sku)}/imports`,
    method: "GET",
  });
};

// Get all inventory items (import records) for a merchant
export const getInventory = (
  merchantId: number,
  filter?: {
    search?: string;
    status?: string;
    per_page?: number;
  },
  page: number = 1
): Promise<any> => {
  return ApiService.fetchData({
    url: `inventory/${merchantId}`,
    method: "GET",
    params: {
      search: filter?.search,
      status: filter?.status,
      per_page: filter?.per_page || 15,
      page: page,
    },
  });
};

export const getInventoryStatistics = (
  merchantId: number
): Promise<InventoryStatistics> => {
  return ApiService.fetchData({
    url: `inventory/${merchantId}/statistics`,
    method: "GET",
  });
};

export const getInventoryItem = (id: number): Promise<InventoryItem> => {
  return ApiService.fetchData({
    url: `inventory/item/${id}`,
    method: "GET",
  });
};

export const getInventoryHistory = (id: number): Promise<any> => {
  return ApiService.fetchData({
    url: `inventory/item/${id}/history`,
    method: "GET",
  });
};

export const importInventory = (
  merchantId: number,
  file: File
): Promise<ImportInventoryResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("merchant_id", merchantId.toString());

  return ApiService.fetchData({
    url: "inventory/import",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const updateInventoryStatus = (
  id: number,
  data: {
    quantity_status: "under_review" | "received" | "missing_quantity";
    missing_quantity_number?: number;
  }
): Promise<InventoryItem> => {
  return ApiService.fetchData({
    url: `inventory/${id}/status`,
    method: "PATCH",
    data: data,
  });
};

export const bulkUpdateInventoryStatus = (
  merchantId: number,
  items: Array<{
    id: number;
    quantity_status: "received" | "missing_quantity";
    missing_quantity_number?: number;
  }>
): Promise<{
  message: string;
  updated: InventoryItem[];
  updated_count: number;
  failed: Array<{ id: number; error: string }>;
  failed_count: number;
}> => {
  return ApiService.fetchData({
    url: `inventory/${merchantId}/bulk-update-status`,
    method: "POST",
    data: { items },
  });
};

export const deleteInventoryItem = (id: number): Promise<any> => {
  return ApiService.fetchData({
    url: `inventory/${id}`,
    method: "DELETE",
  });
};

export const downloadInventoryTemplate = (): Promise<void> => {
  return ApiService.fetchData({
    url: "inventory/template/download",
    method: "GET",
    responseType: "blob",
  }).then((response) => {
    const url = window.URL.createObjectURL(
      new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "inventory-template.xlsx");
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  });
};

export const exportInventory = (merchantId: number): Promise<void> => {
  return ApiService.fetchData({
    url: `inventory/${merchantId}/export`,
    method: "GET",
    responseType: "blob",
  }).then((response) => {
    const url = window.URL.createObjectURL(
      new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `merchant-${merchantId}-inventory-${
        new Date().toISOString().split("T")[0]
      }.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  });
};

export const exportInventoryDashboard = (merchantId: number): Promise<void> => {
  return ApiService.fetchData({
    url: `inventory/${merchantId}/dashboard/export`,
    method: "GET",
    responseType: "blob",
  }).then((response) => {
    const url = window.URL.createObjectURL(
      new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `inventory-dashboard-${merchantId}-${
        new Date().toISOString().split("T")[0]
      }.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  });
};
